import React, { useState, useEffect } from 'react';
import { X, Truck, User, Calendar, MapPin, Clock, FileText, Save, Hash, HelpCircle } from 'lucide-react';
import apiClient from '../services/api';

const AMBER = '#f59e0b', AMBER_DK = '#d97706', VIOLET = '#667eea';

const VehicleAssignmentsForm = ({ onClose, onSave, editData = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cities, setCities] = useState([]);
  const [docNoLoading, setDocNoLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) { console.error('Error parsing session data:', error); return null; }
  };
  const session = getSessionData();
  const companyId = session?.companyid;

  const [formData, setFormData] = useState({
    TripDocNo: '', VehicleID: '', DriverID: '', AssignmentDate: new Date().toISOString().slice(0, 10),
    DepartureTime: '', ArrivalTime: '', OriginCityID: '', DestinationCityID: '', Route: '',
    Purpose: '', Odometer_Start: '', Odometer_End: '', Status: 'Assigned', Remarks: ''
  });

  useEffect(() => {
    loadDropdownData();
    if (editData && editData.AssignmentID) {
      setFormData({
        TripDocNo: editData.TripDocNo || '', VehicleID: editData.VehicleID || '', DriverID: editData.DriverID || '',
        AssignmentDate: editData.AssignmentDate || new Date().toISOString().slice(0, 10),
        DepartureTime: editData.DepartureTime || '', ArrivalTime: editData.ArrivalTime || '',
        OriginCityID: editData.OriginCityID || '', DestinationCityID: editData.DestinationCityID || '',
        Route: editData.Route || '', Purpose: editData.Purpose || '',
        Odometer_Start: editData.Odometer_Start || '', Odometer_End: editData.Odometer_End || '',
        Status: editData.Status || 'Assigned', Remarks: editData.Remarks || ''
      });
    } else { fetchNextDocNo(); }
  }, []);

  const fetchNextDocNo = async () => {
    setDocNoLoading(true);
    try {
      const response = await apiClient.get('/VehicleAssignments.php/next-number');
      if (response.data.success) setFormData(prev => ({ ...prev, TripDocNo: response.data.data.tripDocNo }));
    } catch (err) { console.error('Error fetching next doc number:', err); }
    finally { setDocNoLoading(false); }
  };

  const loadDropdownData = async () => {
    try {
      const [vehicleRes, driverRes, cityRes] = await Promise.all([
        apiClient.get('/Vehicles.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Drivers.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Cities.php').catch(e => ({ data: { success: false } }))
      ]);
      if (vehicleRes.data.success) { const all = vehicleRes.data.data || []; setVehicles(all.filter(v => parseInt(v.IsActive || v.isactive) === 1)); }
      if (driverRes.data.success) { const all = driverRes.data.data || []; setDrivers(all.filter(d => parseInt(d.isactive) === 1)); }
      if (cityRes.data.success) setCities(cityRes.data.data || []);
    } catch (err) { console.error('Error loading dropdown data:', err); setError('Failed to load dropdown data'); }
  };

  const handleChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.VehicleID) { setError('Vehicle is required'); return; }
    if (!formData.DriverID) { setError('Driver is required'); return; }
    if (!formData.AssignmentDate) { setError('Assignment date is required'); return; }
    if (!formData.OriginCityID) { setError('Origin city is required'); return; }
    if (!formData.DestinationCityID) { setError('Destination city is required'); return; }
    setLoading(true); setError('');
    try {
      const dataToSave = {
        ...formData, VehicleID: formData.VehicleID || null, DriverID: formData.DriverID || null,
        OriginCityID: formData.OriginCityID || null, DestinationCityID: formData.DestinationCityID || null,
        Odometer_Start: formData.Odometer_Start || null, Odometer_End: formData.Odometer_End || null
      };
      if (editData && editData.AssignmentID) dataToSave.AssignmentID = editData.AssignmentID;
      await onSave(dataToSave);
    } catch (err) { setError(err.message || 'Failed to save assignment'); }
    finally { setLoading(false); }
  };

  const inpCls = 'w-full px-3 py-2.5 border border-gray-300 rounded-md text-[0.95rem] bg-white transition-all duration-200 outline-none focus:border-violet-400 focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)]';
  const selCls = inpCls + ' cursor-pointer';
  const numCls = inpCls + ' text-right font-mono font-medium tracking-wide h-[42px]';
  const lblCls = 'text-[0.875rem] font-medium text-gray-700';
  const SecHdr = ({ icon: Icon, children, style }) => (
    <h3 className="flex items-center gap-2.5 text-[1.1rem] font-semibold text-gray-800 mb-4 pb-3 border-b-2 border-gray-200" style={style}>
      <Icon size={18} style={{ color: VIOLET }}/>{children}
    </h3>
  );

  if (!companyId) {
    return (
      <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999] p-5 overflow-y-auto">
        <div className="bg-white rounded-xl w-full max-h-[95vh] flex flex-col shadow-2xl relative z-[1]" style={{ maxWidth: 600, animation:'slideIn 0.3s ease-out' }}>
          <div className="text-white px-8 py-7 rounded-t-xl flex justify-between items-center border-b-4 flex-shrink-0 shadow-md" style={{ background:`linear-gradient(135deg,${VIOLET},#764ba2)`, borderColor:'#5a67d8' }}>
            <h2 className="m-0 text-2xl font-bold">Session Required</h2>
            <button onClick={onClose} className="bg-white/20 border-2 border-white/30 text-white w-11 h-11 rounded-[10px] flex items-center justify-center cursor-pointer hover:bg-white/35 hover:rotate-90 hover:scale-105 transition-all duration-200"><X size={20}/></button>
          </div>
          <div className="p-6 text-center">
            <p>Please select a company and financial year from the dashboard.</p>
            <button onClick={onClose} className="mt-4 px-6 py-3 rounded-lg text-[0.95rem] font-semibold cursor-pointer text-white border-2 border-transparent inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(102,126,234,0.4)] transition-all duration-200" style={{ background:`linear-gradient(135deg,${VIOLET},#764ba2)` }}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999] p-5 overflow-y-auto">
      <style>{`
        @keyframes slideIn{from{opacity:0;transform:translateY(-20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>
      <div className="bg-white rounded-xl w-full max-h-[95vh] flex flex-col shadow-2xl relative z-[1]" style={{ maxWidth: 1000, animation:'slideIn 0.3s ease-out' }}>

        {/* Header */}
        <div className="px-8 py-7 rounded-t-xl flex justify-between items-center border-b-4 flex-shrink-0 shadow-md" style={{ background:`linear-gradient(135deg,${AMBER},${AMBER_DK})`, borderColor:AMBER_DK }}>
          <div className="flex items-center gap-6 flex-1">
            <div className="w-[60px] h-[60px] bg-white/30 rounded-2xl flex items-center justify-center shadow-lg"><Truck size={24} color="#ffffff"/></div>
            <div className="text-white text-[1.8rem] font-bold" style={{ textShadow:'0 2px 4px rgba(0,0,0,0.5)' }}>{editData ? 'Edit' : 'New'} Vehicle Assignment</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button type="button" onClick={() => setShowHelp(true)} title="How to fill this form" className="bg-white/20 border-2 border-white/30 text-white w-11 h-11 rounded-[10px] flex items-center justify-center cursor-pointer hover:bg-white/35 transition-all duration-200"><HelpCircle size={20} color="#ffffff"/></button>
            <button onClick={onClose} className="bg-white/20 border-2 border-white/30 text-white w-11 h-11 rounded-[10px] flex items-center justify-center cursor-pointer hover:bg-white/35 transition-all duration-200"><X size={20} color="#ffffff"/></button>
          </div>
        </div>

        {error && <div className="px-4 py-3 rounded-lg mx-[30px] mt-4 flex items-center gap-2 text-[0.95rem] flex-shrink-0 bg-red-100 border border-red-300 text-red-600"><strong>Error:</strong> {error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="flex-1 overflow-y-auto px-[30px] py-6 bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded">

            {/* Trip Doc No */}
            <div className="bg-white rounded-[10px] p-5 mb-4 shadow-sm border border-gray-200">
              <SecHdr icon={Hash}>Trip Document Number</SecHdr>
              <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                <div className="flex flex-col gap-1.5">
                  <label className={lblCls}>Trip Doc No</label>
                  <input type="text" name="TripDocNo" value={docNoLoading ? 'Generating...' : formData.TripDocNo} onChange={handleChange} placeholder="TD-00001" readOnly={!editData}
                    className={inpCls} style={{ fontWeight:700, letterSpacing:'0.5px', color:AMBER_DK, background:editData?undefined:'#fffbeb', cursor:editData?undefined:'default' }}/>
                </div>
              </div>
            </div>

            {/* Vehicle & Driver */}
            <div className="bg-white rounded-[10px] p-5 mb-4 shadow-sm border border-gray-200">
              <SecHdr icon={Truck}>Vehicle &amp; Driver Selection</SecHdr>
              <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                <div className="flex flex-col gap-1.5">
                  <label className={lblCls}>Vehicle *</label>
                  <select name="VehicleID" value={formData.VehicleID} onChange={handleChange} required className={selCls}>
                    <option value="">-- Select Vehicle --</option>
                    {vehicles.map(v => <option key={v.VehicleID} value={v.VehicleID}>{v.RegistrationNo} - {v.VehicleType}</option>)}
                  </select>
                  {vehicles.length === 0 && <small className="text-red-500 text-xs">No active vehicles available</small>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={lblCls}>Driver *</label>
                  <select name="DriverID" value={formData.DriverID} onChange={handleChange} required className={selCls}>
                    <option value="">-- Select Driver --</option>
                    {drivers.map(d => <option key={d.driverid} value={d.driverid}>{d.drivername}</option>)}
                  </select>
                  {drivers.length === 0 && <small className="text-red-500 text-xs">No active drivers available</small>}
                </div>
              </div>
            </div>

            {/* Assignment Details */}
            <div className="bg-white rounded-[10px] p-5 mb-4 shadow-sm border border-gray-200">
              <SecHdr icon={Calendar}>Assignment Details</SecHdr>
              <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                <div className="flex flex-col gap-1.5"><label className={lblCls}>Assignment Date *</label><input type="date" name="AssignmentDate" value={formData.AssignmentDate} onChange={handleChange} required className={inpCls}/></div>
                <div className="flex flex-col gap-1.5">
                  <label className={lblCls}>Status</label>
                  <select name="Status" value={formData.Status} onChange={handleChange} className={selCls}>
                    <option value="Assigned">Assigned</option><option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option><option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Route Information */}
            <div className="bg-white rounded-[10px] p-5 mb-4 shadow-sm border border-gray-200">
              <SecHdr icon={MapPin} style={{ background:'#fef3c7', padding:'8px 12px', borderRadius:6 }}>Route Information</SecHdr>
              <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                <div className="flex flex-col gap-1.5">
                  <label className={lblCls}>Origin City *</label>
                  <select name="OriginCityID" value={formData.OriginCityID} onChange={handleChange} required className={selCls}>
                    <option value="">-- Select Origin City --</option>
                    {cities.map(city => <option key={city.cityid} value={city.cityid}>{city.cityname}</option>)}
                  </select>
                  {cities.length === 0 && <small className="text-red-500 text-xs">No cities available</small>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={lblCls}>Destination City *</label>
                  <select name="DestinationCityID" value={formData.DestinationCityID} onChange={handleChange} required className={selCls}>
                    <option value="">-- Select Destination City --</option>
                    {cities.map(city => <option key={city.cityid} value={city.cityid}>{city.cityname}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5 col-span-full">
                  <label className={lblCls}>Route (Via Locations)</label>
                  <input type="text" name="Route" value={formData.Route} onChange={handleChange} placeholder="e.g., Dubai - Sharjah - Ajman - Abu Dhabi" className={inpCls}/>
                </div>
              </div>
            </div>

            {/* Time & Distance */}
            <div className="bg-white rounded-[10px] p-5 mb-4 shadow-sm border border-gray-200">
              <SecHdr icon={Clock}>Time &amp; Distance</SecHdr>
              <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                <div className="flex flex-col gap-1.5"><label className={lblCls}>Departure Time</label><input type="time" name="DepartureTime" value={formData.DepartureTime} onChange={handleChange} className={inpCls}/></div>
                <div className="flex flex-col gap-1.5"><label className={lblCls}>Arrival Time</label><input type="time" name="ArrivalTime" value={formData.ArrivalTime} onChange={handleChange} className={inpCls}/></div>
                <div className="flex flex-col gap-1.5"><label className={lblCls}>Odometer Start (KM)</label><input type="number" step="0.01" name="Odometer_Start" value={formData.Odometer_Start} onChange={handleChange} placeholder="0.00" min="0" className={numCls}/></div>
                <div className="flex flex-col gap-1.5"><label className={lblCls}>Odometer End (KM)</label><input type="number" step="0.01" name="Odometer_End" value={formData.Odometer_End} onChange={handleChange} placeholder="0.00" min="0" className={numCls}/></div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-white rounded-[10px] p-5 mb-4 shadow-sm border border-gray-200">
              <SecHdr icon={FileText}>Additional Information</SecHdr>
              <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))' }}>
                <div className="flex flex-col gap-1.5 col-span-full"><label className={lblCls}>Purpose of Trip</label><input type="text" name="Purpose" value={formData.Purpose} onChange={handleChange} placeholder="e.g., Delivery to client, Pick up shipment, Maintenance" className={inpCls}/></div>
                <div className="flex flex-col gap-1.5 col-span-full"><label className={lblCls}>Remarks</label><textarea name="Remarks" value={formData.Remarks} onChange={handleChange} rows={3} placeholder="Any additional notes or special instructions..." className={`${inpCls} resize-y min-h-[80px]`}/></div>
              </div>
            </div>

          </div>

          <div className="flex justify-end gap-3 px-[30px] py-5 bg-gray-50 border-t border-gray-200 rounded-b-xl flex-shrink-0">
            <button type="button" onClick={onClose} disabled={loading}
              className="px-6 py-3 rounded-lg text-[0.95rem] font-semibold cursor-pointer bg-white text-gray-500 border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-6 py-3 rounded-lg text-[0.95rem] font-semibold cursor-pointer text-white border-2 border-transparent inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(245,158,11,0.4)] transition-all duration-200"
              style={{ background:`linear-gradient(135deg,${AMBER},${AMBER_DK})` }}>
              {loading ? <><span className="w-[18px] h-[18px] border-2 border-white/30 rounded-full inline-block" style={{ borderTopColor:'#fff', animation:'spin 0.8s linear infinite' }}/> Saving...</> : <><Save size={18}/> {editData ? 'Update' : 'Save'} Assignment</>}
            </button>
          </div>
        </form>
      </div>

      {showHelp && (
        <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-xl w-full max-w-[600px] max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
              style={{ background: `linear-gradient(135deg,${AMBER},${AMBER_DK})` }}>
              <h3 className="flex items-center gap-2 text-lg font-semibold m-0" style={{ color: '#fff' }}>
                <HelpCircle size={18} /> How to Fill This Form
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150"
                style={{ color: '#fff' }}>
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {[
                { title: 'Trip Document Number', desc: 'Auto-generated for new assignments and shown read-only.' },
                { title: 'Vehicle & Driver', desc: 'Select the vehicle and driver for this trip — both required. Only active vehicles and drivers appear in the lists.' },
                { title: 'Assignment Details', desc: 'Set the Assignment Date and current Status (Assigned, In Progress, Completed, Cancelled).' },
                { title: 'Route Information', desc: 'Choose Origin and Destination cities (both required). Add via-locations in the Route field if useful.' },
                { title: 'Time & Distance', desc: 'Enter Departure and Arrival times, and Odometer readings at start and end — the list page calculates distance travelled from these.' },
                { title: 'Additional Information', desc: 'Optionally record the Purpose of the trip and any Remarks.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: `linear-gradient(135deg,${AMBER},${AMBER_DK})` }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                style={{ background: `linear-gradient(135deg,${AMBER},${AMBER_DK})` }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleAssignmentsForm;